import React from "react";
import PropTypes from "prop-types";
import { Database, Globe, RefreshCw, Search, Table } from "lucide-react";
import { getDatabaseButtonText, getTableButtonText, getRegionButtonText } from "../utils/apiService";
import { getTableNames } from "../utils/helpers";

/**
 * Scanner Controls Component
 * Displays database, table, and region selection dropdowns with Analyze button
 */
export const ScannerControls = ({
  databases,
  tables,
  regions,
  regionDisplayMap,
  regionsData,
  selectedDatabase,
  selectedTables,
  selectedRegion,
  loadingDatabases,
  loadingTables,
  loadingRegions,
  loading,
  error,
  showDatabaseDropdown,
  setShowDatabaseDropdown,
  showTableDropdown,
  setShowTableDropdown,
  showRegionDropdown,
  setShowRegionDropdown,
  onDatabaseSelect,
  onTableSelect,
  onRegionSelect,
  onRefresh,
  onAnalyze,
  onSelectAllTables,
  onClearAllTables,
}) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-800 flex items-start gap-2">
          <Database className="w-5 h-5 text-purple-600 mt-1" />
          <div className="flex flex-col">
            <span>PII Data Scanner</span>
            <span className="text-sm font-normal text-gray-500">
              Identify and analyze personally identifiable information in
              your databases
            </span>
          </div>
        </h2>
        <button
          onClick={onRefresh}
          disabled={loadingDatabases || loadingRegions}
          className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all disabled:opacity-50"
          title="Refresh data"
        >
          <RefreshCw
            className={`w-4 h-4 ${loadingDatabases || loadingRegions ? "animate-spin" : ""}`}
          />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <span className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5">⚠</span>
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-4 items-start">
        {/* Database Selection */}
        <div className="relative flex-1 w-full database-dropdown-container">
          <div className="block text-sm font-medium text-gray-700 mb-2">
            Select Database
          </div>
          <button
            type="button"
            onClick={() => setShowDatabaseDropdown(!showDatabaseDropdown)}
            disabled={loadingDatabases || databases.length === 0}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed text-left flex items-center justify-between"
          >
            <span className="text-sm text-gray-700 flex items-center gap-2">
              <Database className="w-4 h-4 text-gray-400" />
              {getDatabaseButtonText(selectedDatabase, loadingDatabases, databases)}
            </span>
            <svg className="w-4 h-4 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showDatabaseDropdown && databases.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {databases.map((db) => (
                <button
                  key={typeof db === "string" ? db : db.name || db.database_name}
                  onClick={() => {
                    onDatabaseSelect(db);
                    setShowDatabaseDropdown(false);
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-purple-50 transition-colors text-sm text-gray-700"
                >
                  {typeof db === "string"
                    ? db
                    : db.name || db.database_name || "Unnamed Database"}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Table Selection */}
        <div className="relative flex-1 w-full table-dropdown-container">
          <div className="block text-sm font-medium text-gray-700 mb-2">
            Select Table(s)
          </div>
          <button
            type="button"
            onClick={() => setShowTableDropdown(!showTableDropdown)}
            disabled={!selectedDatabase || loadingTables || tables.length === 0}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed text-left flex items-center justify-between"
          >
            <span className="text-sm text-gray-700 flex items-center gap-2">
              <Table className="w-4 h-4 text-gray-400" />
              {getTableButtonText(selectedTables, loadingTables, selectedDatabase, tables)}
            </span>
            <svg className="w-4 h-4 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showTableDropdown && tables.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {/* Select All / Deselect All */}
              <div className="sticky top-0 bg-gray-50 border-b border-gray-200 px-4 py-2 flex items-center justify-between">
                <button
                  onClick={() => {
                    const allTableNames = getTableNames(tables);
                    const allSelected = allTableNames.every((t) => selectedTables.includes(t));
                    if (allSelected) {
                      onClearAllTables();
                    } else {
                      onSelectAllTables(allTableNames);
                    }
                  }}
                  className="text-xs font-medium text-purple-600 hover:text-purple-700"
                >
                  {getTableNames(tables).every((t) => selectedTables.includes(t))
                    ? "Clear All"
                    : "Select All"}
                </button>
              </div>

              {tables.map((table) => {
                const tableName =
                  typeof table === "string" ? table : table.name || table.table_name;
                const isSelected = selectedTables.includes(tableName);

                return (
                  <button
                    key={tableName}
                    onClick={() => onTableSelect(tableName)}
                    className="w-full text-left px-4 py-2 hover:bg-purple-50 transition-colors text-sm text-gray-700 flex items-center gap-2"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => undefined}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded"
                    />
                    {tableName || "Unnamed Table"}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Region Selection */}
        <div className="relative flex-1 w-full region-dropdown-container">
          <div className="block text-sm font-medium text-gray-700 mb-2">
            Select Region
          </div>
          <button
            type="button"
            onClick={() => setShowRegionDropdown(!showRegionDropdown)}
            disabled={loadingRegions || regions.length === 0}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed text-left flex items-center justify-between"
          >
            <span className="text-sm text-gray-700 flex items-center gap-2">
              <Globe className="w-4 h-4 text-gray-400" />
              {getRegionButtonText(selectedRegion, loadingRegions, regions, regionDisplayMap)}
            </span>
            <svg className="w-4 h-4 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showRegionDropdown && regions.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {regions.map((region) => (
                <button
                  key={region}
                  onClick={() => {
                    onRegionSelect(region);
                    setShowRegionDropdown(false);
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-purple-50 transition-colors text-sm text-gray-700"
                >
                  {regionDisplayMap[region] || region}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Analyze Button */}
        <div className="w-full lg:w-auto">
          <div className="block text-sm font-medium mb-2">
            &nbsp;
          </div>
          <button
            onClick={onAnalyze}
            disabled={
              !selectedDatabase ||
              selectedTables.length === 0 ||
              !selectedRegion ||
              loading
            }
            className="w-full lg:w-auto px-6 py-2.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex flex-row items-center justify-center gap-2 whitespace-nowrap"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span className="text-sm font-semibold">Analyzing...</span>
              </>
            ) : (
              <>
                <Search className="w-4 h-4 flex-shrink-0" />
                {selectedRegion &&
                  (() => {
                    const regionObj = regionsData.find(
                      (r) => (r.region_code || r.code) === selectedRegion,
                    );
                    const frameworks = regionObj?.compliance_frameworks || [];
                    return frameworks.length > 0 ? (
                      <span className="text-sm font-semibold">
                        Analyze {frameworks.join(" | ")}
                      </span>
                    ) : (
                      <span className="text-sm font-semibold">Analyze PII</span>
                    );
                  })()}
                {!selectedRegion && (
                  <span className="text-sm font-semibold">Analyze PII</span>
                )}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

ScannerControls.propTypes = {
  databases: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])).isRequired,
  tables: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])).isRequired,
  regions: PropTypes.arrayOf(PropTypes.string).isRequired,
  regionDisplayMap: PropTypes.objectOf(PropTypes.string).isRequired,
  regionsData: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedDatabase: PropTypes.string.isRequired,
  selectedTables: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedRegion: PropTypes.string.isRequired,
  loadingDatabases: PropTypes.bool.isRequired,
  loadingTables: PropTypes.bool.isRequired,
  loadingRegions: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  showDatabaseDropdown: PropTypes.bool.isRequired,
  setShowDatabaseDropdown: PropTypes.func.isRequired,
  showTableDropdown: PropTypes.bool.isRequired,
  setShowTableDropdown: PropTypes.func.isRequired,
  showRegionDropdown: PropTypes.bool.isRequired,
  setShowRegionDropdown: PropTypes.func.isRequired,
  onDatabaseSelect: PropTypes.func.isRequired,
  onTableSelect: PropTypes.func.isRequired,
  onRegionSelect: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  onAnalyze: PropTypes.func.isRequired,
  onSelectAllTables: PropTypes.func.isRequired,
  onClearAllTables: PropTypes.func.isRequired,
};
